import { css, cva, sva } from "@stubhub/broadway-foundation/css";

// ---------------------------------------------------------------------------
// Existing styles from prior batches (PartnerLogoImage, etc.)
// ---------------------------------------------------------------------------

export const borderRadiusTop = css({
  borderStartStartRadius: "element.default",
  borderStartEndRadius: "element.default",
});

export const partnerLogoContainer = cva({
  base: {
    width: "[400px]", // Partner logo fixed width — no size token
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
  variants: {
    height: {
      normal: { height: "[245px]" }, // Partner logo height — no size token
      short: { height: "[150px]" }, // Short variant — no size token
    },
    corners: {
      blockStartRounded: {
        borderStartStartRadius: "element.default",
        borderStartEndRadius: "element.default",
        borderEndStartRadius: "primitives.0",
        borderEndEndRadius: "primitives.0",
      },
      none: {
        borderStartStartRadius: "primitives.0",
        borderStartEndRadius: "primitives.0",
        borderEndStartRadius: "primitives.0",
        borderEndEndRadius: "primitives.0",
      },
    },
  },
  defaultVariants: {
    height: "normal",
    corners: "blockStartRounded",
  },
});

// ---------------------------------------------------------------------------
// Batch 15 — PerformerInfoCard multi-slot recipe (sva)
// Replaces PerformerInfoCard.styled.ts
// ---------------------------------------------------------------------------

export const DESKTOP_PERFORMER_INFO_CARD_WIDTH = "400px";

/**
 * Multi-slot recipe for the PerformerInfoCard shell.
 *
 * Slots:
 * - `root`    — Card wrapper (overflow, radius, responsive width)
 * - `content` — Content area (flex column, gap, padding)
 *
 * Follows Broadway `classes` convention for slot targeting.
 */
export const performerInfoCard = sva({
  slots: ["root", "content"],
  className: "performerInfoCard",
  base: {
    root: {
      alignSelf: "flex-start",
      overflowX: "hidden",
      overflowY: "hidden",
      width: "full",
      borderStartStartRadius: "element.default",
      borderStartEndRadius: "element.default",
      borderEndStartRadius: "element.default",
      borderEndEndRadius: "element.default",
      tablet: {
        width: "auto",
        backgroundColor: "[transparent]",
        maxWidth: "[492px]",
        borderStartStartRadius: "primitives.0",
        borderStartEndRadius: "primitives.0",
        borderEndStartRadius: "primitives.0",
        borderEndEndRadius: "primitives.0",
      },
      desktop: {
        width: "[400px]",
        maxWidth: "[none]",
        borderStartStartRadius: "element.default",
        borderStartEndRadius: "element.default",
        borderEndStartRadius: "element.default",
        borderEndEndRadius: "element.default",
      },
    },
    content: {
      display: "flex",
      flexDirection: "column",
      rowGap: "primitives.150",
    },
  },
  variants: {
    cardBorder: {
      none: {},
      bordered: {
        root: {
          borderBlockStartWidth: "thin",
          borderBlockEndWidth: "thin",
          borderInlineStartWidth: "thin",
          borderInlineEndWidth: "thin",
          borderBlockStartStyle: "solid",
          borderBlockEndStyle: "solid",
          borderInlineStartStyle: "solid",
          borderInlineEndStyle: "solid",
          borderBlockStartColor: "border",
          borderBlockEndColor: "border",
          borderInlineStartColor: "border",
          borderInlineEndColor: "border",
          tablet: {
            borderBlockStartWidth: "primitives.0",
            borderBlockEndWidth: "primitives.0",
            borderInlineStartWidth: "primitives.0",
            borderInlineEndWidth: "primitives.0",
          },
          desktop: {
            borderBlockStartWidth: "thin",
            borderBlockEndWidth: "thin",
            borderInlineStartWidth: "thin",
            borderInlineEndWidth: "thin",
          },
        },
      },
    },
    density: {
      default: {
        content: {
          paddingBlockStart: "primitives.200",
          paddingBlockEnd: "primitives.200",
          paddingInlineStart: "primitives.200",
          paddingInlineEnd: "primitives.200",
          tablet: {
            paddingBlockStart: "primitives.0",
            paddingBlockEnd: "primitives.0",
            paddingInlineStart: "primitives.0",
            paddingInlineEnd: "primitives.0",
          },
          desktop: {
            paddingBlockStart: "primitives.200",
            paddingBlockEnd: "primitives.200",
            paddingInlineStart: "primitives.200",
            paddingInlineEnd: "primitives.200",
          },
        },
      },
      reduced: {
        content: {
          paddingBlockStart: "primitives.175",
          paddingBlockEnd: "primitives.175",
          paddingInlineStart: "primitives.175",
          paddingInlineEnd: "primitives.175",
          tablet: {
            paddingBlockStart: "primitives.0",
            paddingBlockEnd: "primitives.0",
            paddingInlineStart: "primitives.0",
            paddingInlineEnd: "primitives.0",
          },
          desktop: {
            paddingBlockStart: "primitives.175",
            paddingBlockEnd: "primitives.175",
            paddingInlineStart: "primitives.175",
            paddingInlineEnd: "primitives.175",
          },
        },
      },
    },
    surface: {
      default: {},
      white: {
        content: {
          backgroundColor: "bg",
          color: "text",
        },
      },
    },
    shadow: {
      none: {},
      floating: {
        root: { boxShadow: "floating" },
      },
    },
  },
  defaultVariants: {
    cardBorder: "none",
    density: "default",
    surface: "default",
    // eslint-disable-next-line panda/prefer-longhand-properties -- variant name, not a CSS property
    shadow: "none",
  },
});

// ---------------------------------------------------------------------------
// Batch 15 — PerformerCardSection (cva)
// Reusable section wrapper inside the info card
// ---------------------------------------------------------------------------

export const performerCardSection = cva({
  base: {},
  variants: {
    spacing: {
      default: {},
      topMargin: {
        marginBlockStart: "primitives.300",
      },
    },
    tone: {
      default: {},
      muted: {
        color: "text.subtle",
      },
    },
    size: {
      default: {},
      small: {
        textStyle: "body.small",
      },
    },
  },
  defaultVariants: {
    spacing: "default",
    tone: "default",
    size: "default",
  },
});

// ---------------------------------------------------------------------------
// Batch 15 — One-off styles (css)
// ---------------------------------------------------------------------------

/** Image with rounded top corners (default state). */
export const styledImageRounded = css({
  borderStartStartRadius: "element.default",
  borderStartEndRadius: "element.default",
  borderEndStartRadius: "primitives.0",
  borderEndEndRadius: "primitives.0",
  display: "block",
});

/** Image with no border radius (when official partner label is above). */
export const styledImageFlat = css({
  borderStartStartRadius: "primitives.0",
  borderStartEndRadius: "primitives.0",
  borderEndStartRadius: "primitives.0",
  borderEndEndRadius: "primitives.0",
  display: "block",
});

/** Genre tag container — flex row with brand color. */
export const genreTagContainerStyle = css({
  display: "flex",
  columnGap: "[5px]",
  color: "text.brand",
});

/** Social media links — no flex grow, vertical spacing. */
export const socialLinksStyle = css({
  flexGrow: 0,
});

/** Spotify player — flex. */
export const spotifyPlayerStyle = css({
  display: "flex",
});
